import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Navigate } from 'react-router-dom';
import {
  ChatWebRTCSignalInput,
  ChatMessageAttachmentInput,
  realtimeApi,
  supportApi,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import {
  SupportChatAttachment,
  SupportChatCallLog,
  SupportChatCallSignal,
  SupportChatConversationDetail,
  SupportChatConversationSummary,
  SupportChatMessage,
  SupportChatUpdateSignal,
  SupportStaffUser,
} from '../types';
import './Pages.css';

const MAX_CHAT_ATTACHMENTS = 5;
const MAX_CHAT_ATTACHMENT_BYTES = 8 * 1024 * 1024;
const CHAT_ATTACHMENT_EXTENSIONS = [
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
  '.txt',
  '.zip',
  '.rar',
];
const CHAT_ATTACHMENT_ACCEPT = CHAT_ATTACHMENT_EXTENSIONS.join(',');
const DEFAULT_WEBRTC_STUN_URL =
  process.env.REACT_APP_WEBRTC_STUN_URL || 'stun:stun.l.google.com:19302';
const DEFAULT_WEBRTC_TURN_URL = (process.env.REACT_APP_WEBRTC_TURN_URL || '').trim();
const DEFAULT_WEBRTC_TURN_USERNAME = (process.env.REACT_APP_WEBRTC_TURN_USERNAME || '').trim();
const DEFAULT_WEBRTC_TURN_CREDENTIAL = (process.env.REACT_APP_WEBRTC_TURN_CREDENTIAL || '').trim();
// Repli TLS (TURNS sur 5349) pour les réseaux qui bloquent l'UDP. Doit utiliser le domaine (le certificat ne correspond pas à l'IP).
const DEFAULT_WEBRTC_TURNS_URL = (process.env.REACT_APP_WEBRTC_TURNS_URL || '').trim();

type SupportChatRealtimeEvent = {
  type?: string;
  request_id?: string;
  success?: boolean;
  error?: string;
  warning?: string;
  conversation_id?: number;
  client_id?: number;
  message_id?: number;
  created_at?: string;
  sender_type?: 'client' | 'staff';
  actor_name?: string;
  is_typing?: boolean;
  call_status?: 'idle' | 'ringing' | 'active';
  action?: string;
  initiator_type?: 'client' | 'staff';
  initiator_staff_id?: number | null;
  initiator_name?: string | null;
  assigned_staff_id?: number | null;
  assigned_staff_name?: string | null;
  reason?: string | null;
  updated_at?: string | null;
  signal_type?: 'offer' | 'answer' | 'ice_candidate';
  actor_type?: 'client' | 'staff' | 'system';
  sdp?: string;
  candidate?: string;
  sdp_mid?: string | null;
  sdp_mline_index?: number | null;
};

type ConversationLoadOptions = {
  silent?: boolean;
  showError?: boolean;
  scrollMode?: 'always' | 'if-near-bottom' | 'none';
};

type PendingSocketRequest = {
  resolve: (payload: SupportChatRealtimeEvent) => void;
  reject: (error: Error) => void;
  timeoutId: number;
};

function isTransientSocketError(message: string): boolean {
  return (
    message === 'WEBSOCKET_UNAVAILABLE' ||
    message === 'WEBSOCKET_TIMEOUT' ||
    message === 'WEBSOCKET_CLOSED' ||
    message === 'WEBSOCKET_SEND_FAILED'
  );
}

function formatDateTime(value?: string | null): string {
  if (!value) {
    return '--';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatTime(value?: string | null): string {
  if (!value) {
    return '--';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatFileSize(size: number): string {
  if (size < 1024) {
    return `${size} o`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} Ko`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
}

function formatDurationSeconds(seconds?: number): string {
  const totalSeconds = Number(seconds || 0);
  if (totalSeconds <= 0) {
    return '0 s';
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;
  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours} h`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} min`);
  }
  if (remainingSeconds > 0 || parts.length === 0) {
    parts.push(`${remainingSeconds} s`);
  }

  return parts.join(' ');
}

function getFileExtension(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex >= 0 ? fileName.slice(lastDotIndex).toLowerCase() : '';
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const markerIndex = result.indexOf(',');
      if (markerIndex === -1) {
        reject(new Error('Encodage du fichier impossible.'));
        return;
      }

      resolve(result.slice(markerIndex + 1));
    };

    reader.onerror = () => reject(new Error(`Lecture du fichier "${file.name}" impossible.`));
    reader.readAsDataURL(file);
  });
}

function getMessageStatusLabel(message: SupportChatMessage): string {
  if (message.sender_type !== 'staff') {
    return '';
  }

  return message.read_at ? 'Vu' : 'Envoye';
}

function getMessageStatusClassName(message: SupportChatMessage): string {
  return message.read_at ? 'seen' : 'sent';
}

function getLatestIsoTimestamp(values: Array<string | null | undefined>): string | null {
  let latestValue: string | null = null;
  let latestTime = 0;

  values.forEach((value) => {
    if (!value) {
      return;
    }

    const parsedTime = new Date(value).getTime();
    if (Number.isNaN(parsedTime)) {
      return;
    }

    if (!latestValue || parsedTime > latestTime) {
      latestValue = value;
      latestTime = parsedTime;
    }
  });

  return latestValue;
}

// Credentials TURN éphémères servis par notre backend (coturn du VPS,
// TTL 1 h). Rafraîchis à la connexion du chat puis à l'approche de
// l'expiration ; en cas d'échec, l'appel reste possible en STUN seul.
let dynamicIceServers: RTCIceServer[] | null = null;
let dynamicIceServersExpiresAt = 0;

async function refreshDynamicIceServers(): Promise<void> {
  try {
    const response = await realtimeApi.getSupportIceServers();
    const servers = response.data?.iceServers;
    const ttlSeconds = Number(response.data?.ttlSeconds || 0);
    if (Array.isArray(servers) && servers.length > 0) {
      dynamicIceServers = servers;
      // Marge de 5 min : ne jamais démarrer un appel avec des credentials
      // en fin de vie (l'allocation TURN doit survivre à l'appel entier).
      dynamicIceServersExpiresAt =
        Date.now() + Math.max(ttlSeconds - 300, 60) * 1000;
    }
  } catch {
    // Repli silencieux sur la configuration statique (STUN seul).
  }
}

function buildWebRTCIceServers(): RTCIceServer[] {
  if (dynamicIceServers && Date.now() < dynamicIceServersExpiresAt) {
    if (Date.now() > dynamicIceServersExpiresAt - 10 * 60 * 1000) {
      void refreshDynamicIceServers();
    }
    return dynamicIceServers;
  }
  void refreshDynamicIceServers();

  const iceServers: RTCIceServer[] = [];

  if (DEFAULT_WEBRTC_STUN_URL) {
    iceServers.push({ urls: [DEFAULT_WEBRTC_STUN_URL] });
  }

  if (DEFAULT_WEBRTC_TURN_URL || DEFAULT_WEBRTC_TURNS_URL) {
    const turnUrls: string[] = [];
    if (DEFAULT_WEBRTC_TURN_URL) turnUrls.push(DEFAULT_WEBRTC_TURN_URL);
    if (DEFAULT_WEBRTC_TURNS_URL) turnUrls.push(DEFAULT_WEBRTC_TURNS_URL);
    const turnServer: RTCIceServer = {
      urls: turnUrls,
    };
    if (DEFAULT_WEBRTC_TURN_USERNAME) {
      turnServer.username = DEFAULT_WEBRTC_TURN_USERNAME;
    }
    if (DEFAULT_WEBRTC_TURN_CREDENTIAL) {
      turnServer.credential = DEFAULT_WEBRTC_TURN_CREDENTIAL;
    }
    iceServers.push(turnServer);
  }

  return iceServers;
}

function getCallLogOutcomeLabel(callLog: SupportChatCallLog): string {
  switch ((callLog.outcome || '').toLowerCase()) {
    case 'completed':
      return 'Appel termine';
    case 'refused':
      return 'Appel refuse';
    case 'missed':
      return 'Appel manque';
    case 'busy':
      return 'Ligne occupee';
    case 'cancelled':
      return 'Appel annule';
    case 'staff_disconnected':
      return 'Staff deconnecte';
    case 'client_disconnected':
      return 'Client deconnecte';
    default:
      return callLog.status === 'active' ? 'Appel en cours' : 'Historique appel';
  }
}

function getCallLogOutcomeClassName(callLog: SupportChatCallLog): string {
  switch ((callLog.outcome || '').toLowerCase()) {
    case 'completed':
      return 'completed';
    case 'busy':
    case 'missed':
      return 'warning';
    case 'refused':
    case 'cancelled':
    case 'staff_disconnected':
    case 'client_disconnected':
      return 'ended';
    default:
      return 'neutral';
  }
}

function getConversationListStateToken(
  nextConversations: SupportChatConversationSummary[]
): string | null {
  return getLatestIsoTimestamp(
    nextConversations.flatMap((conversation) => [
      conversation.updated_at,
      conversation.last_message_at,
      conversation.created_at,
      conversation.closed_at,
    ])
  );
}

function getConversationStateToken(
  nextConversation: SupportChatConversationDetail | null
): string | null {
  if (!nextConversation) {
    return null;
  }

  const messageTimestamps = (nextConversation.messages || []).flatMap((message) => [
    message.updated_at,
    message.created_at,
    message.read_at,
  ]);
  const callLogTimestamps = (nextConversation.call_logs || []).flatMap((callLog) => [
    callLog.updated_at,
    callLog.requested_at,
    callLog.accepted_at,
    callLog.ended_at,
    callLog.created_at,
  ]);

  return getLatestIsoTimestamp([
    nextConversation.updated_at,
    nextConversation.last_message_at,
    nextConversation.created_at,
    nextConversation.closed_at,
    ...messageTimestamps,
    ...callLogTimestamps,
  ]);
}

function getChatStatusClassName(status?: string | null): string {
  return (status || '').toLowerCase() === 'cloture' ? 'closed' : 'open';
}

function isConversationClosed(status?: string | null): boolean {
  return (status || '').toLowerCase() === 'cloture';
}

function hasMessageText(message: SupportChatMessage): boolean {
  return Boolean(message.message_text && message.message_text.trim());
}

function getCallBadgeClassName(signal: SupportChatCallSignal): string {
  if (signal.call_status === 'active') {
    return 'active';
  }

  return signal.initiator_type === 'client' ? 'incoming' : 'outgoing';
}

function getCallBadgeLabel(signal: SupportChatCallSignal): string {
  if (signal.call_status === 'active') {
    return 'Audio actif';
  }

  return signal.initiator_type === 'client' ? 'Sonnerie' : 'Appel sortant';
}

const SupportChat: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const { showToast } = useToast();
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search.trim());
  const [conversations, setConversations] = useState<SupportChatConversationSummary[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);
  const [selectedConversation, setSelectedConversation] =
    useState<SupportChatConversationDetail | null>(null);
  const [messageText, setMessageText] = useState('');
  const [chatAttachments, setChatAttachments] = useState<ChatMessageAttachmentInput[]>([]);
  const [staffUsers, setStaffUsers] = useState<SupportStaffUser[]>([]);
  const [transferStaffId, setTransferStaffId] = useState('');
  const [loadingList, setLoadingList] = useState(true);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [loadingStaffUsers, setLoadingStaffUsers] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [assigningConversation, setAssigningConversation] = useState(false);
  const [updatingConversationStatus, setUpdatingConversationStatus] = useState(false);
  const [downloadingChatAttachmentId, setDownloadingChatAttachmentId] =
    useState<number | null>(null);
  const [isRefreshingSilently, setIsRefreshingSilently] = useState(false);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [remoteTypingLabel, setRemoteTypingLabel] = useState('');
  const [callSignals, setCallSignals] = useState<Record<number, SupportChatCallSignal>>({});
  const [audioStatusLabel, setAudioStatusLabel] = useState('Aucun appel audio actif.');
  const [audioPreparing, setAudioPreparing] = useState(false);
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [videoPreparing, setVideoPreparing] = useState(false);
  const [hasRemoteVideo, setHasRemoteVideo] = useState(false);
  const [processingCallAction, setProcessingCallAction] = useState<
    'request' | 'accept' | 'refuse' | 'end' | null
  >(null);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const threadContainerRef = useRef<HTMLDivElement | null>(null);
  const pendingScrollRef = useRef(false);
  const pendingScrollBehaviorRef = useRef<ScrollBehavior>('auto');
  const pollingInFlightRef = useRef(false);
  const socketRef = useRef<WebSocket | null>(null);
  const socketActiveRef = useRef(false);
  const pendingSocketRequestsRef = useRef<Map<string, PendingSocketRequest>>(new Map());
  const selectedConversationRef = useRef<SupportChatConversationDetail | null>(null);
  const selectedConversationIdRef = useRef<number | null>(null);
  const conversationsCountRef = useRef(0);
  const listStateRef = useRef<string | null>(null);
  const conversationStateRef = useRef<string | null>(null);
  const remoteTypingTimeoutRef = useRef<number | null>(null);
  const localTypingTimeoutRef = useRef<number | null>(null);
  const localTypingActiveRef = useRef(false);
  const callSignalsRef = useRef<Record<number, SupportChatCallSignal>>({});
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const pendingIceCandidatesRef = useRef<RTCIceCandidateInit[]>([]);
  const activeAudioConversationIdRef = useRef<number | null>(null);
  const activeAudioSignalRef = useRef<SupportChatCallSignal | null>(null);
  const offerSentConversationIdRef = useRef<number | null>(null);
  const heartbeatIntervalRef = useRef<number | null>(null);
  const pongTimeoutRef = useRef<number | null>(null);
  const wsAudioWatchdogRef = useRef<number | null>(null);
  const localVideoElementRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoElementRef = useRef<HTMLVideoElement | null>(null);
  const localVideoSenderRef = useRef<RTCRtpSender | null>(null);
  const ringtoneRef = useRef<{ stop: () => void } | null>(null);
  const originalTitleRef = useRef<string>('');

  const canViewChat = Boolean(user);
  const isSupportAdmin = hasPermission('admin', 'access');

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  useEffect(() => {
    selectedConversationIdRef.current = selectedConversationId;
  }, [selectedConversationId]);

  useEffect(() => {
    conversationsCountRef.current = conversations.length;
  }, [conversations.length]);

  useEffect(() => {
    callSignalsRef.current = callSignals;
  }, [callSignals]);

  useEffect(() => {
    if (
      activeAudioConversationIdRef.current &&
      callSignals[activeAudioConversationIdRef.current]
    ) {
      activeAudioSignalRef.current = callSignals[activeAudioConversationIdRef.current];
    } else if (!activeAudioConversationIdRef.current) {
      activeAudioSignalRef.current = null;
    }
  }, [callSignals]);

  const rememberSyncTime = useCallback(() => {
    setLastSyncedAt(new Date().toISOString());
  }, []);

  const clearRemoteTypingIndicator = useCallback(() => {
    if (remoteTypingTimeoutRef.current !== null) {
      window.clearTimeout(remoteTypingTimeoutRef.current);
      remoteTypingTimeoutRef.current = null;
    }
    setRemoteTypingLabel('');
  }, []);

  const clearLocalTypingTimer = useCallback(() => {
    if (localTypingTimeoutRef.current !== null) {
      window.clearTimeout(localTypingTimeoutRef.current);
      localTypingTimeoutRef.current = null;
    }
  }, []);

  const clearSocketHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current !== null) {
      window.clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    if (pongTimeoutRef.current !== null) {
      window.clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = null;
    }
  }, []);

  const acknowledgeSocketHeartbeat = useCallback(() => {
    if (pongTimeoutRef.current !== null) {
      window.clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = null;
    }
  }, []);

  const startSocketHeartbeat = useCallback(() => {
    clearSocketHeartbeat();
    heartbeatIntervalRef.current = window.setInterval(() => {
      const socket = socketRef.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        return;
      }

      acknowledgeSocketHeartbeat();
      pongTimeoutRef.current = window.setTimeout(() => {
        const activeSocket = socketRef.current;
        if (activeSocket && activeSocket.readyState === WebSocket.OPEN) {
          activeSocket.close();
        }
      }, 10000);

      try {
        socket.send(JSON.stringify({ type: 'ping' }));
      } catch {
        acknowledgeSocketHeartbeat();
        socket.close();
      }
    }, 20000);
  }, [acknowledgeSocketHeartbeat, clearSocketHeartbeat]);

  const waitForSocketConnection = useCallback((timeoutMs = 5000) => {
    return new Promise<void>((resolve, reject) => {
      const deadline = Date.now() + timeoutMs;

      const poll = () => {
        const socket = socketRef.current;
        if (socket && socket.readyState === WebSocket.OPEN) {
          resolve();
          return;
        }

        if (Date.now() >= deadline) {
          reject(new Error('WEBSOCKET_UNAVAILABLE'));
          return;
        }

        window.setTimeout(poll, 150);
      };

      poll();
    });
  }, []);

  const forceSocketReconnect = useCallback(() => {
    const socket = socketRef.current;
    if (socket) {
      try {
        socket.close();
      } catch {
        // Ignore close errors during forced reconnect.
      }
    }
  }, []);

  const loadStaffUsers = useCallback(async () => {
    setLoadingStaffUsers(true);
    try {
      const response = await supportApi.getStaffUsers();
      setStaffUsers(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Chargement des membres staff impossible.', 'error');
    } finally {
      setLoadingStaffUsers(false);
    }
  }, [showToast]);

  const rejectPendingSocketRequests = useCallback((message: string) => {
    pendingSocketRequestsRef.current.forEach((pendingRequest) => {
      window.clearTimeout(pendingRequest.timeoutId);
      pendingRequest.reject(new Error(message));
    });
    pendingSocketRequestsRef.current.clear();
  }, []);

  const isThreadNearBottom = useCallback(() => {
    const container = threadContainerRef.current;
    if (!container) {
      return true;
    }

    const remaining = container.scrollHeight - container.scrollTop - container.clientHeight;
    return remaining < 120;
  }, []);

  const queueScrollToBottom = useCallback((behavior: ScrollBehavior) => {
    pendingScrollRef.current = true;
    pendingScrollBehaviorRef.current = behavior;
  }, []);

  const sendMessageViaSocket = useCallback(
    (conversationId: number, nextMessageText: string) =>
      new Promise<SupportChatRealtimeEvent>((resolve, reject) => {
        const socket = socketRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) {
          reject(new Error('WEBSOCKET_UNAVAILABLE'));
          return;
        }

        const requestId = `staff-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const timeoutId = window.setTimeout(() => {
          pendingSocketRequestsRef.current.delete(requestId);
          reject(new Error('WEBSOCKET_TIMEOUT'));
        }, 12000);

        pendingSocketRequestsRef.current.set(requestId, {
          resolve,
          reject,
          timeoutId,
        });

        try {
          socket.send(
            JSON.stringify({
              type: 'send_message',
              request_id: requestId,
              conversation_id: conversationId,
              message_text: nextMessageText,
            })
          );
        } catch (error) {
          window.clearTimeout(timeoutId);
          pendingSocketRequestsRef.current.delete(requestId);
          reject(error instanceof Error ? error : new Error('WEBSOCKET_SEND_FAILED'));
        }
      }),
    []
  );

  const sendTypingSignal = useCallback(
    (conversationId: number | null, isTyping: boolean) => {
      const socket = socketRef.current;
      if (!conversationId || !socket || socket.readyState !== WebSocket.OPEN) {
        if (!isTyping) {
          localTypingActiveRef.current = false;
        }
        return;
      }

      if (localTypingActiveRef.current === isTyping) {
        return;
      }

      try {
        socket.send(
          JSON.stringify({
            type: 'typing',
            conversation_id: conversationId,
            is_typing: isTyping,
          })
        );
        localTypingActiveRef.current = isTyping;
      } catch {
        if (!isTyping) {
          localTypingActiveRef.current = false;
        }
      }
    },
    []
  );

  const scheduleStopTyping = useCallback(
    (conversationId: number | null) => {
      clearLocalTypingTimer();
      localTypingTimeoutRef.current = window.setTimeout(() => {
        sendTypingSignal(conversationId, false);
      }, 1400);
    },
    [clearLocalTypingTimer, sendTypingSignal]
  );

  const applyCallSignal = useCallback((signal: SupportChatCallSignal) => {
    if (!signal.conversation_id) {
      return;
    }

    setCallSignals((previousSignals) => {
      const nextSignals = { ...previousSignals };
      if (signal.call_status === 'idle') {
        delete nextSignals[signal.conversation_id];
      } else {
        nextSignals[signal.conversation_id] = signal;
      }
      return nextSignals;
    });
  }, []);

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.stop();
      ringtoneRef.current = null;
    }
  }, []);

  const startRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      return;
    }
    let audioCtx: AudioContext | null = null;
    try {
      const AudioCtor =
        window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtor) {
        return;
      }
      audioCtx = new AudioCtor();
    } catch {
      return;
    }
    if (!audioCtx) {
      return;
    }
    const ctx = audioCtx;
    let stopped = false;
    const timeouts: number[] = [];

    const playBeep = (frequency: number, duration: number, delay: number) => {
      if (stopped) {
        return;
      }
      try {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        oscillator.type = 'sine';
        oscillator.frequency.value = frequency;
        const start = ctx.currentTime + delay;
        gainNode.gain.setValueAtTime(0, start);
        gainNode.gain.linearRampToValueAtTime(0.15, start + 0.05);
        gainNode.gain.setValueAtTime(0.15, start + duration - 0.05);
        gainNode.gain.linearRampToValueAtTime(0, start + duration);
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        oscillator.start(start);
        oscillator.stop(start + duration);
      } catch {
        // ignore audio errors
      }
    };

    const ringCycle = () => {
      if (stopped) {
        return;
      }
      playBeep(880, 0.4, 0);
      playBeep(660, 0.4, 0.5);
      const id = window.setTimeout(ringCycle, 2000);
      timeouts.push(id);
    };

    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => undefined);
    }
    ringCycle();

    ringtoneRef.current = {
      stop: () => {
        stopped = true;
        timeouts.forEach((id) => window.clearTimeout(id));
        try {
          void ctx.close();
        } catch {
          // ignore close errors
        }
      },
    };
  }, []);

  const sendCallCommandViaSocket = useCallback(
    (
      commandType: 'call_request' | 'call_accept' | 'call_refuse' | 'call_end',
      conversationId: number
    ) =>
      new Promise<SupportChatRealtimeEvent>((resolve, reject) => {
        const socket = socketRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) {
          reject(new Error('WEBSOCKET_UNAVAILABLE'));
          return;
        }

        const requestId = `${commandType}-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2)}`;
        const timeoutId = window.setTimeout(() => {
          pendingSocketRequestsRef.current.delete(requestId);
          reject(new Error('WEBSOCKET_TIMEOUT'));
        }, 12000);

        pendingSocketRequestsRef.current.set(requestId, {
          resolve,
          reject,
          timeoutId,
        });

        try {
          socket.send(
            JSON.stringify({
              type: commandType,
              request_id: requestId,
              conversation_id: conversationId,
            })
          );
        } catch (error) {
          window.clearTimeout(timeoutId);
          pendingSocketRequestsRef.current.delete(requestId);
          reject(error instanceof Error ? error : new Error('WEBSOCKET_SEND_FAILED'));
        }
      }),
    []
  );

  const sendWebRTCSignalViaSocket = useCallback(
    (
      signalType: 'offer' | 'answer' | 'ice_candidate',
      conversationId: number,
      data: ChatWebRTCSignalInput
    ) =>
      new Promise<SupportChatRealtimeEvent>((resolve, reject) => {
        const socket = socketRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) {
          reject(new Error('WEBSOCKET_UNAVAILABLE'));
          return;
        }

        const requestId = `webrtc-${signalType}-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2)}`;
        const timeoutId = window.setTimeout(() => {
          pendingSocketRequestsRef.current.delete(requestId);
          reject(new Error('WEBSOCKET_TIMEOUT'));
        }, 12000);

        pendingSocketRequestsRef.current.set(requestId, {
          resolve,
          reject,
          timeoutId,
        });

        try {
          socket.send(
            JSON.stringify({
              type:
                signalType === 'offer'
                  ? 'webrtc_offer'
                  : signalType === 'answer'
                  ? 'webrtc_answer'
                  : 'webrtc_ice_candidate',
              request_id: requestId,
              conversation_id: conversationId,
              sdp: data.sdp || '',
              candidate: data.candidate || '',
              sdp_mid: data.sdp_mid || '',
              sdp_mline_index:
                typeof data.sdp_mline_index === 'number' ? data.sdp_mline_index : -1,
            })
          );
        } catch (error) {
          window.clearTimeout(timeoutId);
          pendingSocketRequestsRef.current.delete(requestId);
          reject(error instanceof Error ? error : new Error('WEBSOCKET_SEND_FAILED'));
        }
      }),
    []
  );

  const sendWebRTCSignal = useCallback(
    async (
      signalType: 'offer' | 'answer' | 'ice_candidate',
      conversationId: number,
      data: ChatWebRTCSignalInput
    ) => {
      try {
        await waitForSocketConnection();
        await sendWebRTCSignalViaSocket(signalType, conversationId, data);
        return;
      } catch (error: any) {
        const socketMessage = error instanceof Error ? error.message : '';
        if (!isTransientSocketError(socketMessage)) {
          throw error;
        }
      }

      forceSocketReconnect();
      try {
        await supportApi.sendChatWebRTCSignal(conversationId, signalType, data);
      } catch (apiError: any) {
        throw new Error(apiError.response?.data?.error || 'Signal WebRTC impossible.');
      }
    },
    [forceSocketReconnect, sendWebRTCSignalViaSocket, waitForSocketConnection]
  );

  const releaseAudioTransport = useCallback(
    (
      options: {
        stopLocalTracks?: boolean;
        clearActiveConversation?: boolean;
        label?: string;
      } = {}
    ) => {
      const {
        stopLocalTracks = true,
        clearActiveConversation = true,
        label = 'Aucun appel audio actif.',
      } = options;

      const peerConnection = peerConnectionRef.current;
      if (peerConnection) {
        peerConnection.onicecandidate = null;
        peerConnection.ontrack = null;
        peerConnection.onconnectionstatechange = null;
        peerConnection.oniceconnectionstatechange = null;
        try {
          peerConnection.close();
        } catch {
          // Ignore cleanup close failures.
        }
      }
      peerConnectionRef.current = null;

      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = null;
      }

      if (remoteStreamRef.current) {
        remoteStreamRef.current.getTracks().forEach((track) => track.stop());
        remoteStreamRef.current = null;
      }

      if (stopLocalTracks && localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
      }

      pendingIceCandidatesRef.current = [];
      offerSentConversationIdRef.current = null;
      localVideoSenderRef.current = null;
      if (clearActiveConversation) {
        activeAudioConversationIdRef.current = null;
        activeAudioSignalRef.current = null;
      }
      if (localVideoElementRef.current) {
        localVideoElementRef.current.srcObject = null;
      }
      if (remoteVideoElementRef.current) {
        remoteVideoElementRef.current.srcObject = null;
      }
      setVideoEnabled(false);
      setVideoPreparing(false);
      setHasRemoteVideo(false);
      setAudioPreparing(false);
      setAudioMuted(false);
      setAudioStatusLabel(label);
    },
    []
  );

  const flushPendingIceCandidates = useCallback(async (peerConnection: RTCPeerConnection) => {
    if (!peerConnection.remoteDescription || pendingIceCandidatesRef.current.length === 0) {
      return;
    }

    const nextCandidates = [...pendingIceCandidatesRef.current];
    pendingIceCandidatesRef.current = [];

    for (const candidate of nextCandidates) {
      await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
    }
  }, []);

  const enableLocalVideo = useCallback(
    async (conversationId: number) => {
      if (videoEnabled || videoPreparing) {
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        showToast('Le navigateur ne prend pas en charge la video WebRTC.', 'error');
        return;
      }
      const peerConnection = peerConnectionRef.current;
      if (!peerConnection || peerConnection.signalingState === 'closed') {
        showToast('Aucun appel audio actif.', 'warning');
        return;
      }

      setVideoPreparing(true);
      try {
        const videoStream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        });
        const videoTrack = videoStream.getVideoTracks()[0];
        if (!videoTrack) {
          throw new Error('Aucune piste video disponible.');
        }

        if (!localStreamRef.current) {
          localStreamRef.current = new MediaStream();
        }
        localStreamRef.current.addTrack(videoTrack);

        if (localVideoElementRef.current) {
          localVideoElementRef.current.srcObject = localStreamRef.current;
          void localVideoElementRef.current.play().catch(() => undefined);
        }

        const sender = peerConnection.addTrack(videoTrack, localStreamRef.current);
        localVideoSenderRef.current = sender;

        const offer = await peerConnection.createOffer();
        await peerConnection.setLocalDescription(offer);
        await sendWebRTCSignal('offer', conversationId, {
          sdp: offer.sdp || '',
        });

        setVideoEnabled(true);
      } catch (error: any) {
        if (error?.name === 'NotAllowedError') {
          showToast('Acces a la camera refuse. Autorisez le navigateur puis recommencez.', 'error');
        } else if (error?.name === 'NotFoundError') {
          showToast('Aucune camera detectee sur cet appareil.', 'error');
        } else {
          showToast(error?.message || 'Activation de la camera impossible.', 'error');
        }
      } finally {
        setVideoPreparing(false);
      }
    },
    [sendWebRTCSignal, showToast, videoEnabled, videoPreparing]
  );

  const disableLocalVideo = useCallback(
    async (conversationId: number) => {
      const sender = localVideoSenderRef.current;
      const peerConnection = peerConnectionRef.current;

      setVideoEnabled(false);

      if (localStreamRef.current) {
        localStreamRef.current.getVideoTracks().forEach((track) => {
          track.stop();
          localStreamRef.current?.removeTrack(track);
        });
      }
      if (localVideoElementRef.current) {
        localVideoElementRef.current.srcObject = null;
      }

      if (sender && peerConnection && peerConnection.signalingState !== 'closed') {
        try {
          peerConnection.removeTrack(sender);
        } catch {
          // Ignore removeTrack failures (sender may already be detached).
        }
        try {
          const offer = await peerConnection.createOffer();
          await peerConnection.setLocalDescription(offer);
          await sendWebRTCSignal('offer', conversationId, {
            sdp: offer.sdp || '',
          });
        } catch (error: any) {
          showToast(error?.message || 'Renegociation video impossible.', 'error');
        }
      }
      localVideoSenderRef.current = null;
    },
    [sendWebRTCSignal, showToast]
  );

  const toggleVideo = useCallback(() => {
    const conversationId = activeAudioConversationIdRef.current || 0;
    if (!conversationId) {
      return;
    }
    if (videoEnabled) {
      void disableLocalVideo(conversationId);
    } else {
      void enableLocalVideo(conversationId);
    }
  }, [disableLocalVideo, enableLocalVideo, videoEnabled]);

  const getOrCreatePeerConnection = useCallback(
    (conversationId: number) => {
      if (
        activeAudioConversationIdRef.current !== null &&
        activeAudioConversationIdRef.current !== conversationId
      ) {
        throw new Error(
          'Un appel audio est deja actif sur une autre conversation. Terminez-le avant d en demarrer un nouveau.'
        );
      }

      const currentPeerConnection = peerConnectionRef.current;
      if (currentPeerConnection && currentPeerConnection.signalingState !== 'closed') {
        return currentPeerConnection;
      }

      const peerConnection = new RTCPeerConnection({
        iceServers: buildWebRTCIceServers(),
      });

      activeAudioConversationIdRef.current = conversationId;

      peerConnection.onicecandidate = (event) => {
        if (!event.candidate) {
          return;
        }

        void sendWebRTCSignal('ice_candidate', conversationId, {
          candidate: event.candidate.candidate,
          sdp_mid: event.candidate.sdpMid || '',
          sdp_mline_index:
            typeof event.candidate.sdpMLineIndex === 'number'
              ? event.candidate.sdpMLineIndex
              : -1,
        }).catch((error: Error) => {
          showToast(error.message || 'Envoi du candidat ICE impossible.', 'error');
        });
      };

      peerConnection.ontrack = (event) => {
        if (!remoteStreamRef.current) {
          remoteStreamRef.current = new MediaStream();
        }

        const remoteStream = remoteStreamRef.current;
        if (event.streams && event.streams[0]) {
          event.streams[0].getTracks().forEach((track) => {
            if (!remoteStream.getTracks().some((currentTrack) => currentTrack.id === track.id)) {
              remoteStream.addTrack(track);
            }
          });
        } else if (
          !remoteStream.getTracks().some((currentTrack) => currentTrack.id === event.track.id)
        ) {
          remoteStream.addTrack(event.track);
        }

        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = remoteStream;
          void remoteAudioRef.current.play().catch(() => undefined);
        }

        const remoteHasVideo = remoteStream
          .getVideoTracks()
          .some((track) => track.readyState === 'live');
        setHasRemoteVideo(remoteHasVideo);
        if (remoteHasVideo && remoteVideoElementRef.current) {
          remoteVideoElementRef.current.srcObject = remoteStream;
          void remoteVideoElementRef.current.play().catch(() => undefined);
        }

        event.track.addEventListener('ended', () => {
          const stillHasVideo = remoteStream
            .getVideoTracks()
            .some((track) => track.readyState === 'live');
          setHasRemoteVideo(stillHasVideo);
        });

        setAudioStatusLabel('Flux audio connecte.');
      };

      peerConnection.onconnectionstatechange = () => {
        switch (peerConnection.connectionState) {
          case 'connected':
            setAudioStatusLabel('Flux audio connecte.');
            break;
          case 'connecting':
            setAudioStatusLabel('Connexion audio en cours...');
            break;
          case 'disconnected':
            setAudioStatusLabel('Connexion audio interrompue.');
            break;
          case 'failed':
            setAudioStatusLabel('Connexion audio echouee.');
            break;
          case 'closed':
            setAudioStatusLabel('Appel audio termine.');
            break;
          default:
            setAudioStatusLabel('Negociation audio en cours...');
            break;
        }
      };

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => {
          const alreadyAdded = peerConnection
            .getSenders()
            .some((sender) => sender.track?.id === track.id);
          if (!alreadyAdded) {
            peerConnection.addTrack(track, localStreamRef.current as MediaStream);
          }
        });
      }

      peerConnectionRef.current = peerConnection;
      return peerConnection;
    },
    [sendWebRTCSignal, showToast]
  );

  const ensureLocalAudioReady = useCallback(
    async (conversationId: number) => {
      const existingStream = localStreamRef.current;
      if (existingStream && existingStream.getAudioTracks().some((track) => track.readyState === 'live')) {
        const peerConnection = getOrCreatePeerConnection(conversationId);
        existingStream.getTracks().forEach((track) => {
          const alreadyAdded = peerConnection
            .getSenders()
            .some((sender) => sender.track?.id === track.id);
          if (!alreadyAdded) {
            peerConnection.addTrack(track, existingStream);
          }
        });
        return existingStream;
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Le navigateur ne prend pas en charge l audio WebRTC.');
      }

      if (
        !window.isSecureContext &&
        !['localhost', '127.0.0.1'].includes(window.location.hostname)
      ) {
        throw new Error(
          'Le micro du navigateur exige HTTPS ou un acces local pour fonctionner.'
        );
      }

      setAudioPreparing(true);
      setAudioStatusLabel('Activation du micro...');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
        localStreamRef.current = stream;
        setAudioMuted(false);

        const peerConnection = getOrCreatePeerConnection(conversationId);
        stream.getTracks().forEach((track) => {
          const alreadyAdded = peerConnection
            .getSenders()
            .some((sender) => sender.track?.id === track.id);
          if (!alreadyAdded) {
            peerConnection.addTrack(track, stream);
          }
        });

        return stream;
      } catch (error: any) {
        if (error?.name === 'NotAllowedError') {
          throw new Error('Acces au micro refuse. Autorisez le navigateur puis recommencez.');
        }
        if (error?.name === 'NotFoundError') {
          throw new Error('Aucun micro detecte sur cet appareil.');
        }
        throw new Error(error?.message || 'Activation du micro impossible.');
      } finally {
        setAudioPreparing(false);
      }
    },
    [getOrCreatePeerConnection]
  );

  const handleIncomingWebRTCSignal = useCallback(
    async (payload: SupportChatRealtimeEvent) => {
      const conversationId = payload.conversation_id || 0;
      if (!conversationId || payload.actor_type === 'staff' || !payload.signal_type) {
        return;
      }

      const currentSignal = callSignalsRef.current[conversationId];
      if (!currentSignal || currentSignal.call_status !== 'active') {
        return;
      }

      if (
        activeAudioConversationIdRef.current &&
        activeAudioConversationIdRef.current !== conversationId
      ) {
        return;
      }

      activeAudioConversationIdRef.current = conversationId;
      activeAudioSignalRef.current = currentSignal;

      try {
        await ensureLocalAudioReady(conversationId);
        const peerConnection = getOrCreatePeerConnection(conversationId);

        if (payload.signal_type === 'offer') {
          setAudioStatusLabel('Offre audio recue. Preparation de la reponse...');
          await peerConnection.setRemoteDescription({
            type: 'offer',
            sdp: payload.sdp || '',
          });
          await flushPendingIceCandidates(peerConnection);
          const answer = await peerConnection.createAnswer();
          await peerConnection.setLocalDescription(answer);
          await sendWebRTCSignal('answer', conversationId, {
            sdp: answer.sdp || '',
          });
          setAudioStatusLabel('Reponse audio envoyee.');
          return;
        }

        if (payload.signal_type === 'answer') {
          if (payload.sdp) {
            await peerConnection.setRemoteDescription({
              type: 'answer',
              sdp: payload.sdp,
            });
            await flushPendingIceCandidates(peerConnection);
            setAudioStatusLabel('Connexion audio en cours...');
          }
          return;
        }

        if (payload.signal_type === 'ice_candidate' && payload.candidate) {
          const iceCandidate: RTCIceCandidateInit = {
            candidate: payload.candidate,
            sdpMid: payload.sdp_mid || undefined,
            sdpMLineIndex:
              typeof payload.sdp_mline_index === 'number'
                ? payload.sdp_mline_index
                : undefined,
          };

          if (peerConnection.remoteDescription) {
            await peerConnection.addIceCandidate(new RTCIceCandidate(iceCandidate));
          } else {
            pendingIceCandidatesRef.current.push(iceCandidate);
          }
        }
      } catch (error: any) {
        showToast(error.message || 'Traitement audio WebRTC impossible.', 'error');
      }
    },
    [
      ensureLocalAudioReady,
      flushPendingIceCandidates,
      getOrCreatePeerConnection,
      sendWebRTCSignal,
      showToast,
    ]
  );

  const startAudioOfferIfNeeded = useCallback(
    async (conversationId: number) => {
      if (offerSentConversationIdRef.current === conversationId) {
        return;
      }

      await ensureLocalAudioReady(conversationId);
      const peerConnection = getOrCreatePeerConnection(conversationId);
      setAudioStatusLabel('Preparation de l offre audio...');
      const offer = await peerConnection.createOffer({
        offerToReceiveAudio: true,
      });
      await peerConnection.setLocalDescription(offer);
      offerSentConversationIdRef.current = conversationId;
      await sendWebRTCSignal('offer', conversationId, {
        sdp: offer.sdp || '',
      });
      setAudioStatusLabel('Offre audio envoyee.');
    },
    [ensureLocalAudioReady, getOrCreatePeerConnection, sendWebRTCSignal]
  );

  const toggleAudioMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) {
      return;
    }

    const nextMuted = !audioMuted;
    stream.getAudioTracks().forEach((track) => {
      track.enabled = !nextMuted;
    });
    setAudioMuted(nextMuted);
  }, [audioMuted]);

  const loadConversations = useCallback(
    async (
      preferredConversationId?: number | null,
      options: ConversationLoadOptions = {}
    ) => {
      const { silent = false, showError = !silent } = options;
      const shouldShowLoading = !silent && conversationsCountRef.current === 0;

      if (shouldShowLoading) {
        setLoadingList(true);
      }

      try {
        const response = await supportApi.getChatConversations(
          deferredSearch ? { search: deferredSearch } : undefined
        );
        const nextConversations = Array.isArray(response.data) ? response.data : [];
        listStateRef.current = getConversationListStateToken(nextConversations);
        setConversations(nextConversations);
        setSelectedConversationId((currentId) => {
          const targetId = preferredConversationId ?? currentId;
          if (targetId && nextConversations.some((conversation) => conversation.id === targetId)) {
            return targetId;
          }

          return nextConversations.length > 0 ? nextConversations[0].id : null;
        });
        rememberSyncTime();
      } catch (err: any) {
        if (showError) {
          showToast(err.response?.data?.error || 'Chargement des chats impossible.', 'error');
        }
      } finally {
        if (shouldShowLoading) {
          setLoadingList(false);
        }
      }
    },
    [deferredSearch, rememberSyncTime, showToast]
  );

  const loadConversation = useCallback(
    async (
      conversationId: number | null,
      options: ConversationLoadOptions = {}
    ) => {
      if (!conversationId) {
        conversationStateRef.current = null;
        setSelectedConversation(null);
        return;
      }

      const { silent = false, showError = !silent, scrollMode } = options;
      const effectiveScrollMode = scrollMode || (silent ? 'if-near-bottom' : 'always');
      const shouldShowLoading =
        !silent &&
        (!selectedConversationRef.current || selectedConversationRef.current.id !== conversationId);

      if (shouldShowLoading) {
        setLoadingConversation(true);
      }

      const shouldScrollAfterLoad =
        effectiveScrollMode === 'always' ||
        (effectiveScrollMode === 'if-near-bottom' && isThreadNearBottom());

      try {
        const response = await supportApi.getChatConversationById(conversationId);
        const nextConversation = response.data as SupportChatConversationDetail;
        conversationStateRef.current = getConversationStateToken(nextConversation);
        setSelectedConversation(nextConversation);
        if (shouldScrollAfterLoad) {
          queueScrollToBottom(silent ? 'auto' : 'smooth');
        }
        rememberSyncTime();
      } catch (err: any) {
        if (!silent) {
          conversationStateRef.current = null;
          setSelectedConversation(null);
        }
        if (showError) {
          showToast(
            err.response?.data?.error || 'Chargement de la conversation impossible.',
            'error'
          );
        }
      } finally {
        if (shouldShowLoading) {
          setLoadingConversation(false);
        }
      }
    },
    [isThreadNearBottom, queueScrollToBottom, rememberSyncTime, showToast]
  );

  useEffect(() => {
    void loadConversations(undefined, { silent: conversationsCountRef.current > 0 });
  }, [loadConversations]);

  useEffect(() => {
    void loadStaffUsers();
  }, [loadStaffUsers]);

  useEffect(() => {
    void loadConversation(selectedConversationId, {
      scrollMode: 'always',
    });
  }, [loadConversation, selectedConversationId]);

  useEffect(() => {
    setMessageText('');
    setChatAttachments([]);
    clearRemoteTypingIndicator();
    clearLocalTypingTimer();
    localTypingActiveRef.current = false;
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  }, [clearLocalTypingTimer, clearRemoteTypingIndicator, selectedConversationId]);

  useEffect(() => {
    if (selectedConversation?.assigned_staff_id) {
      setTransferStaffId(String(selectedConversation.assigned_staff_id));
      return;
    }

    setTransferStaffId(isSupportAdmin ? '' : String(user?.id || ''));
  }, [isSupportAdmin, selectedConversation?.assigned_staff_id, user?.id]);

  useEffect(() => {
    let cancelled = false;
    let reconnectTimer: number | null = null;
    let socket: WebSocket | null = null;

    const connectSocket = () => {
      // Pré-chauffe les credentials TURN pendant que le chat se connecte :
      // le premier appel de la session part avec un relais déjà prêt.
      void refreshDynamicIceServers();
      const socketUrl = realtimeApi.buildSupportChatSocketUrl();
      if (!socketUrl || cancelled) {
        return;
      }

      try {
        socket = new WebSocket(socketUrl);
        socketRef.current = socket;
      } catch {
        socketRef.current = null;
        reconnectTimer = window.setTimeout(connectSocket, 2000);
        return;
      }

      socket.onopen = () => {
        socketActiveRef.current = true;
        setIsSocketConnected(true);
        acknowledgeSocketHeartbeat();
        startSocketHeartbeat();
        rememberSyncTime();
        if (wsAudioWatchdogRef.current !== null) {
          window.clearTimeout(wsAudioWatchdogRef.current);
          wsAudioWatchdogRef.current = null;
        }
      };

      socket.onmessage = (event) => {
        let payload: SupportChatRealtimeEvent | null = null;
        try {
          payload = JSON.parse(event.data) as SupportChatRealtimeEvent;
        } catch {
          return;
        }

        if (
          ((payload.type && payload.type.endsWith('_result')) || payload.type === 'error') &&
          payload.request_id
        ) {
          const pendingRequest = pendingSocketRequestsRef.current.get(payload.request_id);
          if (pendingRequest) {
            window.clearTimeout(pendingRequest.timeoutId);
            pendingSocketRequestsRef.current.delete(payload.request_id);
            if (payload.success === false || payload.error) {
              pendingRequest.reject(new Error(payload.error || 'Envoi du message impossible.'));
            } else {
              pendingRequest.resolve(payload);
            }
          }
          return;
        }

        if (payload.type === 'connected' || payload.type === 'pong') {
          acknowledgeSocketHeartbeat();
          return;
        }

        if (!payload?.type || payload.type !== 'chat_changed') {
          if (payload.type === 'call_signal' && payload.conversation_id) {
            const callSignal = payload as SupportChatCallSignal;
            applyCallSignal(callSignal);
            if (
              payload.action === 'requested' &&
              payload.initiator_type === 'client' &&
              payload.conversation_id !== selectedConversationIdRef.current
            ) {
              showToast(
                `${payload.initiator_name || 'Client'} demande un appel audio.`,
                'warning'
              );
            }
            return;
          }

          if (payload.type === 'webrtc_signal') {
            void handleIncomingWebRTCSignal(payload);
            return;
          }

          if (
            payload.type === 'typing' &&
            payload.sender_type === 'client' &&
            payload.conversation_id === selectedConversationIdRef.current
          ) {
            if (!payload.is_typing) {
              clearRemoteTypingIndicator();
              return;
            }

            setRemoteTypingLabel(
              `${selectedConversationRef.current?.client_name || 'Client'} ecrit...`
            );
            if (remoteTypingTimeoutRef.current !== null) {
              window.clearTimeout(remoteTypingTimeoutRef.current);
            }
            remoteTypingTimeoutRef.current = window.setTimeout(() => {
              setRemoteTypingLabel('');
              remoteTypingTimeoutRef.current = null;
            }, 2600);
          }
          return;
        }

        const currentConversationId = selectedConversationIdRef.current;
        void loadConversations(currentConversationId, {
          silent: true,
          showError: false,
        });

        if (!currentConversationId) {
          return;
        }

        if (!payload.conversation_id || payload.conversation_id === currentConversationId) {
          clearRemoteTypingIndicator();
          void loadConversation(currentConversationId, {
            silent: true,
            showError: false,
            scrollMode: 'if-near-bottom',
          });
        }
      };

      socket.onerror = () => {
        socket?.close();
      };

      socket.onclose = () => {
        socketRef.current = null;
        socketActiveRef.current = false;
        clearSocketHeartbeat();
        setIsSocketConnected(false);
        setProcessingCallAction(null);
        rejectPendingSocketRequests('WEBSOCKET_CLOSED');
        if (wsAudioWatchdogRef.current !== null) {
          window.clearTimeout(wsAudioWatchdogRef.current);
        }
        wsAudioWatchdogRef.current = window.setTimeout(() => {
          wsAudioWatchdogRef.current = null;
          if (activeAudioConversationIdRef.current !== null) {
            releaseAudioTransport({ label: 'Connexion perdue, appel termine.' });
          }
        }, 15000);
        if (!cancelled) {
          reconnectTimer = window.setTimeout(connectSocket, 2000);
        }
      };
    };

    connectSocket();

    return () => {
      cancelled = true;
      socketRef.current = null;
      socketActiveRef.current = false;
      clearSocketHeartbeat();
      clearRemoteTypingIndicator();
      clearLocalTypingTimer();
      localTypingActiveRef.current = false;
      setIsSocketConnected(false);
      rejectPendingSocketRequests('WEBSOCKET_CLOSED');
      if (wsAudioWatchdogRef.current !== null) {
        window.clearTimeout(wsAudioWatchdogRef.current);
        wsAudioWatchdogRef.current = null;
      }
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      socket?.close();
    };
  }, [
    applyCallSignal,
    acknowledgeSocketHeartbeat,
    clearLocalTypingTimer,
    clearRemoteTypingIndicator,
    clearSocketHeartbeat,
    handleIncomingWebRTCSignal,
    loadConversation,
    loadConversations,
    rejectPendingSocketRequests,
    releaseAudioTransport,
    rememberSyncTime,
    startSocketHeartbeat,
    showToast,
  ]);

  useEffect(() => {
    return () => {
      clearLocalTypingTimer();
      sendTypingSignal(selectedConversationId, false);
      clearRemoteTypingIndicator();
    };
  }, [clearLocalTypingTimer, clearRemoteTypingIndicator, selectedConversationId, sendTypingSignal]);

  useEffect(() => {
    let cancelled = false;

    const waitFor = (delayMs: number) =>
      new Promise<void>((resolve) => {
        window.setTimeout(resolve, delayMs);
      });

    const runRealtimeRefresh = async () => {
      while (!cancelled) {
        if (
          document.visibilityState === 'hidden' ||
          pollingInFlightRef.current ||
          sendingMessage ||
          socketActiveRef.current
        ) {
          await waitFor(1000);
          continue;
        }

        pollingInFlightRef.current = true;
        setIsRefreshingSilently(true);
        try {
          const response = await supportApi.waitForChatUpdates({
            search: deferredSearch || undefined,
            conversation_id: selectedConversationId || undefined,
            list_after: listStateRef.current || undefined,
            list_count: conversationsCountRef.current,
            conversation_after: conversationStateRef.current || undefined,
          });
          if (cancelled) {
            return;
          }

          const signal = response.data as SupportChatUpdateSignal;
          rememberSyncTime();

          if (signal.list_state) {
            listStateRef.current = signal.list_state;
          } else if (signal.list_count === 0) {
            listStateRef.current = null;
          }

          if (!signal.conversation_exists) {
            conversationStateRef.current = null;
          } else if (signal.conversation_state) {
            conversationStateRef.current = signal.conversation_state;
          }

          if (signal.list_changed) {
            await loadConversations(selectedConversationId, {
              silent: true,
              showError: false,
            });
          }

          if (selectedConversationId && signal.conversation_changed) {
            await loadConversation(selectedConversationId, {
              silent: true,
              showError: false,
              scrollMode: 'if-near-bottom',
            });
          }
        } catch {
          if (cancelled) {
            return;
          }
          await waitFor(2000);
        } finally {
          pollingInFlightRef.current = false;
          if (!cancelled) {
            setIsRefreshingSilently(false);
          }
        }
      }
    };

    void runRealtimeRefresh();

    return () => {
      cancelled = true;
    };
  }, [
    deferredSearch,
    loadConversation,
    loadConversations,
    rememberSyncTime,
    selectedConversationId,
    sendingMessage,
  ]);

  useEffect(() => {
    const currentActiveConversationId = activeAudioConversationIdRef.current;
    const currentActiveSignal = currentActiveConversationId
      ? callSignals[currentActiveConversationId] || null
      : null;
    const selectedSignal = selectedConversationId ? callSignals[selectedConversationId] || null : null;
    const effectiveSignal = currentActiveSignal || selectedSignal;
    const effectiveConversationId =
      currentActiveConversationId || selectedConversationId || 0;

    if (!effectiveSignal || effectiveSignal.call_status === 'idle') {
      releaseAudioTransport();
      return;
    }

    if (!effectiveConversationId) {
      return;
    }

    activeAudioConversationIdRef.current = effectiveConversationId;
    activeAudioSignalRef.current = effectiveSignal;

    if (effectiveSignal.call_status === 'ringing') {
      offerSentConversationIdRef.current = null;
      setAudioStatusLabel(
        effectiveSignal.initiator_type === 'client'
          ? 'Invitation audio recue. En attente de votre reponse.'
          : 'Demande audio envoyee. En attente de validation.'
      );
      return;
    }

    if (effectiveSignal.initiator_type === 'staff') {
      void startAudioOfferIfNeeded(effectiveConversationId).catch((error: Error) => {
        showToast(error.message || 'Preparation de l appel audio impossible.', 'error');
      });
      return;
    }

    void ensureLocalAudioReady(effectiveConversationId)
      .then(() => {
        setAudioStatusLabel('Appel accepte. Reception du flux audio...');
      })
      .catch((error: Error) => {
        showToast(error.message || 'Activation du micro impossible.', 'error');
      });
  }, [
    callSignals,
    ensureLocalAudioReady,
    releaseAudioTransport,
    selectedConversationId,
    showToast,
    startAudioOfferIfNeeded,
  ]);

  useEffect(() => {
    if (!pendingScrollRef.current) {
      return;
    }

    threadEndRef.current?.scrollIntoView({ behavior: pendingScrollBehaviorRef.current });
    pendingScrollRef.current = false;
  }, [selectedConversation?.id, selectedConversation?.messages?.length]);

  useEffect(() => {
    return () => {
      releaseAudioTransport();
    };
  }, [releaseAudioTransport]);

  // Sonnerie d'appel entrant : detecte tout call_signal 'ringing' avec initiator='client'
  useEffect(() => {
    const incomingCalls = Object.values(callSignals).filter(
      (signal) =>
        signal.call_status === 'ringing' && signal.initiator_type === 'client'
    );
    const hasIncomingRingingCall = incomingCalls.length > 0;

    if (hasIncomingRingingCall) {
      if (!originalTitleRef.current) {
        originalTitleRef.current = document.title;
      }
      startRingtone();
      document.title = `Appel entrant - ${originalTitleRef.current || 'Winalco'}`;
    } else {
      stopRingtone();
      if (originalTitleRef.current) {
        document.title = originalTitleRef.current;
      }
    }

    return () => {
      stopRingtone();
      if (originalTitleRef.current) {
        document.title = originalTitleRef.current;
      }
    };
  }, [callSignals, startRingtone, stopRingtone]);

  const selectedConversationSummary = useMemo(() => {
    if (!selectedConversationId) {
      return null;
    }

    return conversations.find((conversation) => conversation.id === selectedConversationId) || null;
  }, [conversations, selectedConversationId]);

  const activeAssignedStaffId =
    selectedConversation?.assigned_staff_id ?? selectedConversationSummary?.assigned_staff_id ?? null;
  const activeStatus = selectedConversation?.status || selectedConversationSummary?.status || 'Ouvert';
  const conversationIsClosed = isConversationClosed(activeStatus);
  const selectedCallSignal = selectedConversationId ? callSignals[selectedConversationId] || null : null;
  const hasSelectedAudioSignal = Boolean(
    selectedCallSignal &&
      (selectedCallSignal.call_status === 'ringing' ||
        selectedCallSignal.call_status === 'active')
  );
  const isIncomingAudioRequest = Boolean(
    selectedCallSignal?.call_status === 'ringing' &&
      selectedCallSignal.initiator_type === 'client'
  );
  const isOutgoingAudioRequest = Boolean(
    selectedCallSignal?.call_status === 'ringing' &&
      selectedCallSignal.initiator_type === 'staff'
  );
  const isAudioSessionActive = selectedCallSignal?.call_status === 'active';
  const canReply =
    Boolean(selectedConversationId) &&
    (isSupportAdmin || !activeAssignedStaffId || activeAssignedStaffId === user?.id);
  const canTakeConversation = Boolean(selectedConversationId && user?.id && !activeAssignedStaffId);
  const canTransferConversation = Boolean(
    selectedConversationId && (isSupportAdmin || activeAssignedStaffId === user?.id)
  );
  const canChangeStatus = Boolean(
    selectedConversationId && (isSupportAdmin || activeAssignedStaffId === user?.id)
  );
  const canStartAudioCall = Boolean(
    selectedConversationId && canReply && !hasSelectedAudioSignal
  );
  const canAcceptAudioCall = Boolean(
    selectedConversationId && canReply && isIncomingAudioRequest
  );
  const canRefuseAudioCall = Boolean(
    selectedConversationId &&
      isIncomingAudioRequest &&
      (isSupportAdmin || activeAssignedStaffId === user?.id)
  );
  const canEndAudioCall = Boolean(
    selectedConversationId && canReply && hasSelectedAudioSignal
  );
  const transferButtonLabel = activeAssignedStaffId ? 'Transferer' : 'Affecter';

  const handleChatAttachmentSelection = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';

    if (selectedFiles.length === 0) {
      return;
    }

    const remainingSlots = MAX_CHAT_ATTACHMENTS - chatAttachments.length;
    if (remainingSlots <= 0) {
      showToast(`Maximum ${MAX_CHAT_ATTACHMENTS} pieces jointes par message.`, 'warning');
      return;
    }

    const filesToRead = selectedFiles.slice(0, remainingSlots);
    const skippedReasons: string[] = [];

    if (selectedFiles.length > filesToRead.length) {
      skippedReasons.push(`maximum ${MAX_CHAT_ATTACHMENTS} fichiers atteint`);
    }

    const validFiles = filesToRead.filter((file) => {
      const extension = getFileExtension(file.name);
      if (!CHAT_ATTACHMENT_EXTENSIONS.includes(extension)) {
        skippedReasons.push(`${file.name}: format non autorise`);
        return false;
      }

      if (file.size > MAX_CHAT_ATTACHMENT_BYTES) {
        skippedReasons.push(`${file.name}: fichier trop volumineux`);
        return false;
      }

      return true;
    });

    try {
      const encodedAttachments = await Promise.all(
        validFiles.map(async (file): Promise<ChatMessageAttachmentInput> => ({
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
          file_size: file.size,
          base64_content: await readFileAsBase64(file),
        }))
      );

      setChatAttachments((prev) => [...prev, ...encodedAttachments]);

      if (skippedReasons.length > 0) {
        showToast(skippedReasons.join(' | '), 'warning');
      }
    } catch (error: any) {
      showToast(error.message || 'Lecture des pieces jointes impossible.', 'error');
    }
  };

  const handleRemoveChatAttachment = (indexToRemove: number) => {
    setChatAttachments((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleDownloadChatAttachment = async (
    messageId: number,
    attachment: SupportChatAttachment
  ) => {
    setDownloadingChatAttachmentId(attachment.id);
    try {
      const response = await supportApi.downloadChatAttachment(messageId, attachment.id);
      const blob = new Blob([response.data], {
        type: attachment.content_type || 'application/octet-stream',
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = attachment.file_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Telechargement impossible.', 'error');
    } finally {
      setDownloadingChatAttachmentId(null);
    }
  };

  const refreshSelectedConversation = useCallback(
    async (conversationId: number | null, scrollMode: ConversationLoadOptions['scrollMode']) => {
      await loadConversations(conversationId, { silent: true });
      if (conversationId) {
        await loadConversation(conversationId, { silent: true, scrollMode });
      }
    },
    [loadConversation, loadConversations]
  );

  const handleAssignConversation = useCallback(
    async (assignedStaffId: number, successMessage: string) => {
      if (!selectedConversationId) {
        return;
      }

      setAssigningConversation(true);
      try {
        await supportApi.assignChatConversation(selectedConversationId, assignedStaffId);
        showToast(successMessage, 'success');
        await refreshSelectedConversation(selectedConversationId, 'none');
      } catch (err: any) {
        showToast(err.response?.data?.error || 'Affectation du chat impossible.', 'error');
      } finally {
        setAssigningConversation(false);
      }
    },
    [refreshSelectedConversation, selectedConversationId, showToast]
  );

  const handleTakeConversation = async () => {
    if (!user?.id) {
      return;
    }

    await handleAssignConversation(user.id, 'Conversation prise en charge.');
  };

  const handleTransferConversation = async () => {
    const nextStaffId = Number(transferStaffId || '0');
    if (nextStaffId <= 0) {
      showToast('Selectionnez un membre staff pour affecter ce chat.', 'warning');
      return;
    }

    await handleAssignConversation(nextStaffId, 'Affectation du chat enregistree.');
  };

  const handleToggleConversationStatus = async () => {
    if (!selectedConversationId) {
      return;
    }

    setUpdatingConversationStatus(true);
    try {
      const nextStatus = conversationIsClosed ? 'Ouvert' : 'Cloture';
      await supportApi.updateChatConversationStatus(selectedConversationId, nextStatus);
      showToast(
        conversationIsClosed ? 'Conversation reouverte.' : 'Conversation cloturee.',
        'success'
      );
      await refreshSelectedConversation(selectedConversationId, 'none');
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Mise a jour du statut impossible.', 'error');
    } finally {
      setUpdatingConversationStatus(false);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedConversationId) {
      return;
    }

    const trimmedMessage = messageText.trim();
    if (!trimmedMessage && chatAttachments.length === 0) {
      showToast('Le message du chat ou au moins une piece jointe est obligatoire.', 'warning');
      return;
    }

    setSendingMessage(true);
    try {
      let warningMessage = '';
      sendTypingSignal(selectedConversationId, false);
      clearLocalTypingTimer();

      if (chatAttachments.length > 0) {
        const response = await supportApi.sendChatMessage(
          selectedConversationId,
          trimmedMessage,
          chatAttachments
        );
        warningMessage = response.data.warning || '';
      } else if (socketRef.current?.readyState === WebSocket.OPEN) {
        try {
          const socketResponse = await sendMessageViaSocket(
            selectedConversationId,
            trimmedMessage
          );
          warningMessage = socketResponse.warning || '';
        } catch (socketError: any) {
          const socketMessage = socketError instanceof Error ? socketError.message : '';
          if (
            socketMessage === 'WEBSOCKET_UNAVAILABLE' ||
            socketMessage === 'WEBSOCKET_TIMEOUT' ||
            socketMessage === 'WEBSOCKET_CLOSED' ||
            socketMessage === 'WEBSOCKET_SEND_FAILED'
          ) {
            const response = await supportApi.sendChatMessage(
              selectedConversationId,
              trimmedMessage
            );
            warningMessage = response.data.warning || '';
          } else {
            throw socketError;
          }
        }
      } else {
        const response = await supportApi.sendChatMessage(selectedConversationId, trimmedMessage);
        warningMessage = response.data.warning || '';
      }

      setMessageText('');
      setChatAttachments([]);
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = '';
      }
      if (warningMessage) {
        showToast(warningMessage, 'warning');
      } else {
        showToast('Message envoye dans le chat.', 'success');
      }

      await refreshSelectedConversation(selectedConversationId, 'always');
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Envoi du message impossible.', 'error');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleManualRefresh = async () => {
    setIsManualRefreshing(true);
    try {
      await loadStaffUsers();
      await loadConversations(selectedConversationId, { silent: true });
      if (selectedConversationId) {
        await loadConversation(selectedConversationId, {
          silent: true,
          scrollMode: 'none',
        });
      }
    } finally {
      setIsManualRefreshing(false);
    }
  };

  const runCallCommand = useCallback(
    async (
      commandType: 'call_request' | 'call_accept' | 'call_refuse' | 'call_end',
      actionKey: 'request' | 'accept' | 'refuse' | 'end',
      successMessage: string
    ) => {
      if (!selectedConversationId) {
        return;
      }

      setProcessingCallAction(actionKey);
      try {
        await waitForSocketConnection();
        await sendCallCommandViaSocket(commandType, selectedConversationId);
        showToast(successMessage, 'success');
      } catch (error: any) {
        const socketMessage = error instanceof Error ? error.message : '';
        if (isTransientSocketError(socketMessage)) {
          forceSocketReconnect();
          try {
            await waitForSocketConnection();
            await sendCallCommandViaSocket(commandType, selectedConversationId);
            showToast(successMessage, 'success');
          } catch (retryError: any) {
            const retryMessage =
              retryError instanceof Error ? retryError.message : '';
            if (isTransientSocketError(retryMessage)) {
              try {
                await supportApi.sendChatCallCommand(selectedConversationId, actionKey);
                showToast(successMessage, 'success');
              } catch (apiError: any) {
                showToast(
                  apiError.response?.data?.error || 'Signalisation audio indisponible pour le moment.',
                  'error'
                );
              }
            } else {
              showToast(retryMessage || 'Commande audio impossible.', 'error');
            }
          }
        } else {
          showToast(socketMessage || 'Commande audio impossible.', 'error');
        }
      } finally {
        setProcessingCallAction(null);
      }
    },
    [
      forceSocketReconnect,
      selectedConversationId,
      sendCallCommandViaSocket,
      showToast,
      waitForSocketConnection,
    ]
  );

  const handleRequestAudioCall = async () => {
    if (!selectedConversationId) {
      return;
    }

    try {
      await ensureLocalAudioReady(selectedConversationId);
    } catch (error: any) {
      showToast(error.message || 'Activation du micro impossible.', 'error');
      return;
    }

    await runCallCommand('call_request', 'request', 'Demande d appel audio envoyee.');
  };

  const handleAcceptAudioCall = async () => {
    if (!selectedConversationId) {
      return;
    }

    try {
      await ensureLocalAudioReady(selectedConversationId);
    } catch (error: any) {
      showToast(error.message || 'Activation du micro impossible.', 'error');
      return;
    }

    await runCallCommand('call_accept', 'accept', 'Demande d appel audio acceptee.');
  };

  const handleRefuseAudioCall = async () => {
    await runCallCommand('call_refuse', 'refuse', 'Demande d appel audio refusee.');
    releaseAudioTransport();
  };

  const handleEndAudioCall = async () => {
    await runCallCommand('call_end', 'end', 'Session audio terminee.');
    releaseAudioTransport();
  };

  const handleMessageTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = event.target.value;
    setMessageText(nextValue);

    if (!canReply) {
      return;
    }

    const activeConversationId = selectedConversationIdRef.current;
    if (!nextValue.trim()) {
      clearLocalTypingTimer();
      sendTypingSignal(activeConversationId, false);
      return;
    }

    sendTypingSignal(activeConversationId, true);
    scheduleStopTyping(activeConversationId);
  };

  const handleMessageComposerBlur = () => {
    clearLocalTypingTimer();
    sendTypingSignal(selectedConversationIdRef.current, false);
  };

  if (!canViewChat) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Chat Support</h1>
      </div>

      <div className="support-chat-shell">
        <aside className="support-chat-sidebar">
          <div className="support-chat-sidebar-header">
            <div className="support-chat-sidebar-title">
              <h2>Conversations</h2>
              <button
                type="button"
                className="btn btn-secondary support-chat-refresh-btn"
                onClick={handleManualRefresh}
                disabled={sendingMessage || isManualRefreshing}
              >
                {isManualRefreshing ? 'Actualisation...' : 'Actualiser'}
              </button>
            </div>
            <input
              className="search-input"
              type="text"
              placeholder="Recherche client, email, telephone"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <div className="support-chat-sync-note">
              <span>
                {isSocketConnected
                  ? 'WebSocket actif'
                  : isRefreshingSilently
                  ? 'Canal de secours en attente...'
                  : 'Canal de secours pret'}
              </span>
              <small>
                {lastSyncedAt ? `Derniere sync ${formatTime(lastSyncedAt)}` : 'Sync en attente'}
              </small>
            </div>
          </div>

          {loadingList ? (
            <div className="loading">Chargement des chats...</div>
          ) : conversations.length === 0 ? (
            <div className="support-empty-state compact">Aucune conversation chat.</div>
          ) : (
            <div className="support-chat-conversation-list">
              {conversations.map((conversation) => (
                <button
                  key={conversation.id}
                  type="button"
                  className={`support-chat-conversation-item${
                    selectedConversationId === conversation.id ? ' active' : ''
                  }`}
                  onClick={() => setSelectedConversationId(conversation.id)}
                >
                  <div className="support-chat-conversation-top">
                    <strong>{conversation.client_name}</strong>
                    <span>{formatTime(conversation.last_message_at || conversation.created_at)}</span>
                  </div>
                  <div className="support-chat-conversation-meta">
                    <span>{conversation.telephone || conversation.client_email || 'Sans contact'}</span>
                    <small>
                      {conversation.assigned_staff_name
                        ? `Affecte a ${conversation.assigned_staff_name}`
                        : 'Non affecte'}
                    </small>
                  </div>
                  <div className="support-chat-conversation-bottom">
                    <p>{conversation.last_message_text || 'Conversation disponible.'}</p>
                    <div className="support-chat-conversation-flags">
                      {callSignals[conversation.id] && (
                        <span
                          className={`support-chat-call-badge ${getCallBadgeClassName(
                            callSignals[conversation.id]
                          )}`}
                        >
                          {getCallBadgeLabel(callSignals[conversation.id])}
                        </span>
                      )}
                      <span
                        className={`support-chat-status-badge ${getChatStatusClassName(
                          conversation.status
                        )}`}
                      >
                        {conversation.status || 'Ouvert'}
                      </span>
                      {conversation.unread_count > 0 && (
                        <span className="support-chat-unread-badge">{conversation.unread_count}</span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </aside>

        <section className="support-chat-panel">
          {!selectedConversationId ? (
            <div className="support-empty-state">Selectionnez une conversation.</div>
          ) : loadingConversation ? (
            <div className="loading">Chargement de la conversation...</div>
          ) : !selectedConversation ? (
            <div className="support-empty-state">Conversation introuvable.</div>
          ) : (
            <>
              <div className="support-chat-header">
                <div>
                  <h2>{selectedConversation.client_name}</h2>
                  <p>
                    {selectedConversation.telephone || '--'} ·{' '}
                    {selectedConversation.client_email || '--'}
                  </p>
                  <div className="support-chat-workflow">
                    <span
                      className={`support-chat-status-badge ${getChatStatusClassName(activeStatus)}`}
                    >
                      {activeStatus}
                    </span>
                    {canStartAudioCall && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleRequestAudioCall}
                        disabled={processingCallAction !== null || audioPreparing}
                      >
                        {processingCallAction === 'request'
                          ? 'Demande audio...'
                          : audioPreparing
                          ? 'Preparation micro...'
                          : 'Demander appel audio'}
                      </button>
                    )}
                    {canAcceptAudioCall && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleAcceptAudioCall}
                        disabled={processingCallAction !== null || audioPreparing}
                      >
                        {processingCallAction === 'accept'
                          ? 'Validation...'
                          : audioPreparing
                          ? 'Preparation micro...'
                          : 'Accepter appel'}
                      </button>
                    )}
                    {canRefuseAudioCall && (
                      <button
                        type="button"
                        className="btn btn-warning"
                        onClick={handleRefuseAudioCall}
                        disabled={processingCallAction !== null}
                      >
                        {processingCallAction === 'refuse' ? 'Refus...' : 'Refuser appel'}
                      </button>
                    )}
                    {canEndAudioCall && (
                      <button
                        type="button"
                        className="btn btn-warning"
                        onClick={handleEndAudioCall}
                        disabled={processingCallAction !== null}
                      >
                        {processingCallAction === 'end' ? 'Cloture audio...' : 'Terminer appel'}
                      </button>
                    )}
                    {isAudioSessionActive && localStreamRef.current && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={toggleAudioMute}
                      >
                        {audioMuted ? 'Activer micro' : 'Couper micro'}
                      </button>
                    )}
                    {isAudioSessionActive && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={toggleVideo}
                        disabled={videoPreparing}
                      >
                        {videoPreparing
                          ? 'Activation camera...'
                          : videoEnabled
                          ? 'Couper camera'
                          : 'Activer camera'}
                      </button>
                    )}
                    {canTakeConversation && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleTakeConversation}
                        disabled={assigningConversation}
                      >
                        {assigningConversation ? 'Affectation...' : 'Prendre en charge'}
                      </button>
                    )}
                    {canTransferConversation && (
                      <div className="support-chat-transfer-box">
                        <select
                          value={transferStaffId}
                          onChange={(event) => setTransferStaffId(event.target.value)}
                          disabled={assigningConversation || loadingStaffUsers}
                        >
                          <option value="">
                            {loadingStaffUsers ? 'Chargement staff...' : 'Choisir un staff'}
                          </option>
                          {staffUsers.map((staffUser) => (
                            <option key={staffUser.id} value={staffUser.id}>
                              {staffUser.user_name}
                              {staffUser.role_name ? ` (${staffUser.role_name})` : ''}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleTransferConversation}
                          disabled={
                            assigningConversation ||
                            loadingStaffUsers ||
                            !transferStaffId ||
                            Number(transferStaffId) === Number(activeAssignedStaffId || 0)
                          }
                        >
                          {assigningConversation ? 'Enregistrement...' : transferButtonLabel}
                        </button>
                      </div>
                    )}
                    {canChangeStatus && (
                      <button
                        type="button"
                        className={`btn ${conversationIsClosed ? 'btn-secondary' : 'btn-warning'}`}
                        onClick={handleToggleConversationStatus}
                        disabled={updatingConversationStatus}
                      >
                        {updatingConversationStatus
                          ? 'Mise a jour...'
                          : conversationIsClosed
                          ? 'Reouvrir'
                          : 'Cloturer'}
                      </button>
                    )}
                  </div>
                </div>
                <div className="support-chat-header-side">
                  <span>
                    {selectedConversation.assigned_staff_name
                      ? `Affecte a ${selectedConversation.assigned_staff_name}`
                      : 'Conversation non affectee'}
                  </span>
                  {selectedConversation.closed_by_staff_name && conversationIsClosed && (
                    <small>
                      Cloturee par {selectedConversation.closed_by_staff_name}{' '}
                      {selectedConversation.closed_at
                        ? `le ${formatDateTime(selectedConversation.closed_at)}`
                        : ''}
                    </small>
                  )}
                  <small>
                    Derniere activite{' '}
                    {formatDateTime(
                      selectedConversation.last_message_at || selectedConversation.updated_at
                    )}
                  </small>
                </div>
              </div>

              {conversationIsClosed && (
                <div className="support-chat-banner">
                  Conversation cloturee. Une nouvelle reponse ou le bouton reouvrir rouvrira le chat.
                </div>
              )}

              {selectedCallSignal && (
                <div
                  className={`support-chat-call-banner ${getCallBadgeClassName(
                    selectedCallSignal
                  )}`}
                >
                  <strong>
                    {isIncomingAudioRequest
                      ? `${selectedConversation.client_name} demande un appel audio.`
                      : isOutgoingAudioRequest
                      ? 'Demande d appel audio envoyee au client.'
                      : 'Appel audio accepte.'}
                  </strong>
                  <span>
                    {isAudioSessionActive
                      ? audioStatusLabel
                      : audioPreparing
                      ? 'Activation du micro en cours...'
                      : 'Le micro sera ouvert des validation de l appel.'}
                  </span>
                </div>
              )}

              {(selectedCallSignal || activeAudioConversationIdRef.current) && (
                <div className="support-chat-banner">{audioStatusLabel}</div>
              )}
              <audio ref={remoteAudioRef} autoPlay playsInline hidden />

              {(videoEnabled || hasRemoteVideo) && (
                <div
                  style={{
                    position: 'fixed',
                    top: 20,
                    right: 20,
                    width: 320,
                    zIndex: 1100,
                    background: '#000',
                    borderRadius: 10,
                    overflow: 'hidden',
                    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.35)',
                  }}
                >
                  <video
                    ref={remoteVideoElementRef}
                    autoPlay
                    playsInline
                    style={{
                      width: '100%',
                      display: hasRemoteVideo ? 'block' : 'none',
                      background: '#000',
                    }}
                  />
                  {!hasRemoteVideo && (
                    <div
                      style={{
                        padding: '60px 20px',
                        color: '#fff',
                        textAlign: 'center',
                        fontSize: 13,
                      }}
                    >
                      En attente de la video distante...
                    </div>
                  )}
                  {videoEnabled && (
                    <video
                      ref={localVideoElementRef}
                      autoPlay
                      playsInline
                      muted
                      style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        width: 100,
                        height: 75,
                        background: '#000',
                        borderRadius: 4,
                        border: '2px solid #fff',
                        objectFit: 'cover',
                      }}
                    />
                  )}
                </div>
              )}

              {!!selectedConversation.call_logs?.length && (
                <div className="support-chat-call-history">
                  <div className="support-chat-call-history-header">
                    <strong>Historique appels</strong>
                    <span>{selectedConversation.call_logs.length} evenement(s)</span>
                  </div>
                  <div className="support-chat-call-history-list">
                    {selectedConversation.call_logs.map((callLog) => (
                      <div key={callLog.id} className="support-chat-call-log-item">
                        <div className="support-chat-call-log-top">
                          <span
                            className={`support-chat-call-log-badge ${getCallLogOutcomeClassName(
                              callLog
                            )}`}
                          >
                            {getCallLogOutcomeLabel(callLog)}
                          </span>
                          <span>{formatDateTime(callLog.requested_at)}</span>
                        </div>
                        <div className="support-chat-call-log-meta">
                          <span>
                            {callLog.initiator_type === 'client'
                              ? `${selectedConversation.client_name} a initie l appel`
                              : `${callLog.initiator_name || 'Le support'} a initie l appel`}
                          </span>
                          {callLog.assigned_staff_name && (
                            <span>Traite par {callLog.assigned_staff_name}</span>
                          )}
                          {Number(callLog.duration_seconds || 0) > 0 && (
                            <span>Duree: {formatDurationSeconds(callLog.duration_seconds)}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="support-chat-thread" ref={threadContainerRef}>
                {(selectedConversation.messages || []).length === 0 ? (
                  <div className="support-empty-state compact">
                    Aucun message pour le moment.
                  </div>
                ) : (
                  (selectedConversation.messages || []).map((message) => (
                    <div
                      key={message.id}
                      className={`support-chat-bubble-row ${
                        message.sender_type === 'staff' ? 'own' : 'other'
                      }`}
                    >
                      <div className="support-chat-bubble">
                        <div className="support-chat-bubble-meta">
                          <strong>
                            {message.sender_type === 'staff'
                              ? message.staff_name || 'Equipe support'
                              : selectedConversation.client_name}
                          </strong>
                          <span>{formatDateTime(message.created_at)}</span>
                        </div>
                        {hasMessageText(message) && (
                          <div className="support-chat-bubble-text">{message.message_text}</div>
                        )}
                        {message.attachments && message.attachments.length > 0 && (
                          <div className="support-chat-attachments">
                            {message.attachments.map((attachment) => (
                              <button
                                key={attachment.id}
                                type="button"
                                className="support-chat-attachment-chip"
                                onClick={() =>
                                  handleDownloadChatAttachment(message.id, attachment)
                                }
                                disabled={downloadingChatAttachmentId === attachment.id}
                              >
                                <span>{attachment.file_name}</span>
                                <small>
                                  {downloadingChatAttachmentId === attachment.id
                                    ? 'Telechargement...'
                                    : formatFileSize(attachment.file_size)}
                                </small>
                              </button>
                            ))}
                          </div>
                        )}
                        {message.sender_type === 'staff' && (
                          <div
                            className={`support-chat-bubble-status ${getMessageStatusClassName(
                              message
                            )}`}
                          >
                            {getMessageStatusLabel(message)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
                {remoteTypingLabel && (
                  <div className="support-chat-typing-row">
                    <div className="support-chat-typing-indicator">
                      <span className="support-chat-typing-dots" aria-hidden="true">
                        <span />
                        <span />
                        <span />
                      </span>
                      <span>{remoteTypingLabel}</span>
                    </div>
                  </div>
                )}
                <div ref={threadEndRef} />
              </div>

              <div className="support-chat-composer">
                <textarea
                  rows={4}
                  value={messageText}
                  onChange={handleMessageTextChange}
                  onBlur={handleMessageComposerBlur}
                  placeholder={
                    canReply
                      ? 'Saisissez votre message au client.'
                      : 'Conversation deja prise en charge par un autre staff.'
                  }
                  disabled={sendingMessage || !canReply}
                />

                <input
                  ref={attachmentInputRef}
                  type="file"
                  multiple
                  accept={CHAT_ATTACHMENT_ACCEPT}
                  className="support-chat-hidden-input"
                  onChange={handleChatAttachmentSelection}
                />

                {chatAttachments.length > 0 && (
                  <div className="support-chat-selected-attachments">
                    {chatAttachments.map((attachment, index) => (
                      <div key={`${attachment.file_name}-${index}`} className="support-chat-selected-item">
                        <div>
                          <strong>{attachment.file_name}</strong>
                          <small>{formatFileSize(attachment.file_size)}</small>
                        </div>
                        <button
                          type="button"
                          className="btn-icon support-chat-remove-attachment"
                          onClick={() => handleRemoveChatAttachment(index)}
                          disabled={sendingMessage}
                          title="Retirer la piece jointe"
                          aria-label={`Retirer ${attachment.file_name}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="support-chat-composer-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => attachmentInputRef.current?.click()}
                    disabled={
                      sendingMessage || !canReply || chatAttachments.length >= MAX_CHAT_ATTACHMENTS
                    }
                  >
                    Ajouter une piece jointe
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSendMessage}
                    disabled={
                      sendingMessage || !canReply || (!messageText.trim() && chatAttachments.length === 0)
                    }
                  >
                    {sendingMessage ? 'Envoi...' : 'Envoyer'}
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default SupportChat;
